import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  X,
  Eye,
  Download,
  ExternalLink,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileArchive,
  FileVideo,
  Presentation,
  Link as LinkIcon,
  Upload,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { uploadFilesToStorage } from '../../../services/upload';

const C = {
  ink: '#0F172A',
  sub: '#475569',
  faint: '#94A3B8',
  border: '#E2E8F0',
  bg: '#F8FAFC',
  card: '#FFFFFF',
  info: '#2563EB',
  infoSoft: '#EFF6FF',
  warning: '#D97706',
  warningSoft: '#FFF9EB',
};

type AttachmentKind = 'image' | 'pdf' | 'doc' | 'sheet' | 'slide' | 'archive' | 'video' | 'link';

const EXT_KINDS: Record<string, AttachmentKind> = {
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
  bmp: 'image',
  svg: 'image',
  heic: 'image',
  pdf: 'pdf',
  doc: 'doc',
  docx: 'doc',
  odt: 'doc',
  rtf: 'doc',
  txt: 'doc',
  xls: 'sheet',
  xlsx: 'sheet',
  csv: 'sheet',
  ods: 'sheet',
  ppt: 'slide',
  pptx: 'slide',
  odp: 'slide',
  mp4: 'video',
  m4v: 'video',
  mov: 'video',
  webm: 'video',
  zip: 'archive',
  rar: 'archive',
  '7z': 'archive',
  tar: 'archive',
  gz: 'archive',
  tgz: 'archive',
};

const KIND_META: Record<Exclude<AttachmentKind, 'link'>, { label: string; icon: any; color: string }> = {
  image: { label: 'Image', icon: FileImage, color: '#7C3AED' },
  pdf: { label: 'PDF', icon: FileText, color: '#DC2626' },
  doc: { label: 'Document', icon: FileText, color: '#2563EB' },
  sheet: { label: 'Spreadsheet', icon: FileSpreadsheet, color: '#16A34A' },
  slide: { label: 'Presentation', icon: Presentation, color: '#D97706' },
  archive: { label: 'Archive', icon: FileArchive, color: '#0F766E' },
  video: { label: 'Video', icon: FileVideo, color: '#DB2777' },
};

interface ParsedAttachment {
  value: string;
  isUrl: boolean;
  ext: string;
  kind: AttachmentKind;
  name: string;
}

export const parseAttachment = (raw: string): ParsedAttachment => {
  const value = (raw || '').trim();
  let pathname = value;
  let isUrl = false;
  try {
    const url = new URL(value);
    isUrl = url.protocol === 'http:' || url.protocol === 'https:';
    pathname = url.pathname;
  } catch {
    isUrl = false;
  }

  const lastSegment = pathname.split('/').filter(Boolean).pop() || '';
  const ext = lastSegment.includes('.') ? lastSegment.split('.').pop()!.toLowerCase() : '';
  const kind: AttachmentKind = isUrl ? EXT_KINDS[ext] || 'link' : 'link';

  let name = value;
  if (isUrl && lastSegment) {
    try {
      name = decodeURIComponent(lastSegment);
    } catch {
      name = lastSegment;
    }
  }

  return { value, isUrl, ext, kind, name };
};

const officeViewerUrl = (url: string) =>
  `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`;

const GCS_HOST = 'storage.googleapis.com';

export const toDownloadUrl = (raw: string): string => {
  try {
    const url = new URL(raw);
    const isGcs = url.hostname === GCS_HOST || url.hostname.endsWith(`.${GCS_HOST}`);
    if (!isGcs || url.pathname.startsWith('/download/storage/v1/')) return raw;

    let bucket = '';
    let objectPath = '';
    if (url.hostname === GCS_HOST) {
      const segments = url.pathname.replace(/^\//, '').split('/');
      bucket = segments.shift() || '';
      objectPath = segments.join('/');
    } else {
      bucket = url.hostname.slice(0, -(GCS_HOST.length + 1));
      objectPath = url.pathname.replace(/^\//, '');
    }
    if (!bucket || !objectPath) return raw;

    const object = encodeURIComponent(decodeURIComponent(objectPath));
    return `https://${GCS_HOST}/download/storage/v1/b/${bucket}/o/${object}?alt=media`;
  } catch {
    return raw;
  }
};

const AttachmentPreviewDialog: React.FC<{
  attachment: ParsedAttachment;
  open: boolean;
  onClose: () => void;
  onDownload: () => void;
  downloading: boolean;
}> = ({ attachment, open, onClose, onDownload, downloading }) => {
  const { kind, value, name } = attachment;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontSize: 15, fontWeight: 800, color: C.ink, pr: 6 }}
      >
        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close preview" sx={{ position: 'absolute', right: 12, top: 12 }}>
          <X size={18} color={C.sub} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, bgcolor: C.bg }}>
        {kind === 'image' ? (
          <Box sx={{ display: 'grid', placeItems: 'center', p: 2, minHeight: 320 }}>
            <Box
              component="img"
              src={value}
              alt={name}
              sx={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '8px', border: `1px solid ${C.border}` }}
            />
          </Box>
        ) : kind === 'video' ? (
          <Box sx={{ display: 'grid', placeItems: 'center', p: 2, minHeight: 320, bgcolor: '#000' }}>
            <Box
              component="video"
              src={value}
              controls
              preload="metadata"
              sx={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '8px' }}
            />
          </Box>
        ) : (
          <>
            {kind !== 'pdf' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.25,
                  bgcolor: C.warningSoft,
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                <Typography sx={{ fontSize: 12, color: C.warning, fontWeight: 600 }}>
                  Preview is rendered by an external viewer and only works for publicly accessible files. Use Download
                  if it stays blank.
                </Typography>
              </Box>
            )}
            <Box
              component="iframe"
              title={`Preview of ${name}`}
              src={kind === 'pdf' ? value : officeViewerUrl(value)}
              sx={{ width: '100%', height: '70vh', border: 'none', bgcolor: C.card }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={() => window.open(value, '_blank', 'noopener,noreferrer')}
          startIcon={<ExternalLink size={15} />}
          sx={{ textTransform: 'none', fontWeight: 600, color: C.card }}
        >
          Open in new tab
        </Button>
        <Button
          variant="contained"
          disabled={downloading}
          onClick={onDownload}
          startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <Download size={15} />}
          sx={{
            bgcolor: C.info,
            color: '#fff',
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            '&:hover': { bgcolor: '#1D4ED8' },
          }}
        >
          {downloading ? 'Downloading...' : 'Download'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RemoveButton: React.FC<{ onRemove: () => void; name: string }> = ({ onRemove, name }) => (
  <Tooltip title="Remove">
    <IconButton size="small" onClick={onRemove} aria-label={`Remove ${name}`}>
      <Trash2 size={14} color={C.faint} />
    </IconButton>
  </Tooltip>
);

const AttachmentItem: React.FC<{ raw: string; onRemove?: () => void }> = ({ raw, onRemove }) => {
  const attachment = parseAttachment(raw);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (attachment.kind === 'link') {
    const { value, isUrl } = attachment;
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          maxWidth: '100%',
          px: 1.25,
          py: 0.75,
          borderRadius: '8px',
          bgcolor: C.bg,
          border: `1px solid ${C.border}`,
        }}
      >
        <LinkIcon size={13} color={C.faint} style={{ flexShrink: 0 }} />
        {isUrl ? (
          <Typography
            component="a"
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: C.info,
              textDecoration: 'none',
              wordBreak: 'break-all',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {value}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, color: C.sub, wordBreak: 'break-all' }}>{value}</Typography>
        )}
        {onRemove && <RemoveButton onRemove={onRemove} name={value} />}
      </Box>
    );
  }

  const meta = KIND_META[attachment.kind];
  const Icon = meta.icon;
  const canPreview = attachment.kind !== 'archive';


  const saveAs = (href: string, revoke = false) => {
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = attachment.name;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    if (revoke) setTimeout(() => URL.revokeObjectURL(href), 1000);
  };

  const handleDownload = async () => {
    setDownloading(true);
    const downloadUrl = toDownloadUrl(attachment.value);
    try {
      const res = await fetch(downloadUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      saveAs(URL.createObjectURL(await res.blob()), true);
    } catch (error) {
      console.error('attachment-download-failed', error);
      saveAs(downloadUrl);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          p: 1.25,
          borderRadius: '10px',
          bgcolor: C.card,
          border: `1px solid ${C.border}`,
          minWidth: 240,
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '8px',
            bgcolor: `${meta.color}14`,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={17} color={meta.color} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Tooltip title={attachment.value}>
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 700,
                color: C.ink,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {attachment.name}
            </Typography>
          </Tooltip>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.faint, textTransform: 'uppercase' }}>
            {meta.label}
            {attachment.ext ? ` · ${attachment.ext}` : ''}
          </Typography>
        </Box>
        {canPreview && (
          <Tooltip title="Preview">
            <IconButton size="small" onClick={() => setPreviewOpen(true)} aria-label={`Preview ${attachment.name}`}>
              <Eye size={15} color={C.sub} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Download">
          <span>
            <IconButton
              size="small"
              disabled={downloading}
              onClick={handleDownload}
              aria-label={`Download ${attachment.name}`}
            >
              {downloading ? <CircularProgress size={14} /> : <Download size={15} color={C.sub} />}
            </IconButton>
          </span>
        </Tooltip>
        {onRemove && <RemoveButton onRemove={onRemove} name={attachment.name} />}
      </Box>

      {previewOpen && (
        <AttachmentPreviewDialog
          attachment={attachment}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          onDownload={handleDownload}
          downloading={downloading}
        />
      )}
    </>
  );
};

const AttachmentList: React.FC<{
  attachments: string[];
  onRemove?: (index: number) => void;
}> = ({ attachments, onRemove }) => {
  const items = (attachments || [])
    .map((a, index) => ({ value: (a || '').trim(), index }))
    .filter((a) => a.value);
  if (!items.length) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {items.map((att) => (
        <AttachmentItem
          key={`${att.value}-${att.index}`}
          raw={att.value}
          onRemove={onRemove ? () => onRemove(att.index) : undefined}
        />
      ))}
    </Box>
  );
};


export const ATTACHMENT_ACCEPT =
  'image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx';

// Campaign creatives can be video, so message-level uploads take mp4 too.
export const MEDIA_ATTACHMENT_ACCEPT = `${ATTACHMENT_ACCEPT},video/mp4,video/quicktime,video/webm,.mp4,.m4v,.mov,.webm`;

// Cohort briefs arrive as whatever the requester has on hand, archives included.
export const COHORT_ATTACHMENT_ACCEPT = `${ATTACHMENT_ACCEPT},.txt,.zip,.rar,.7z,.tar,.gz`;

// Both ceilings sit under the rule server's 25 MB multer limit on
// /users/storage/upload. Documents stay at the older, tighter 5 MB.
const MAX_ATTACHMENT_MB = 5;
const MAX_VIDEO_MB = 25;

const isVideoFile = (file: File) => {
  if (file.type.startsWith('video/')) return true;
  const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
  return EXT_KINDS[ext] === 'video';
};

export const ATTACHMENT_FOLDER = 'journey-requests/attachments';

export const COHORT_ATTACHMENT_FOLDER = 'journey-requests/cohorts';

export const AttachmentUploadButton: React.FC<{
  onUploaded: (urls: string[]) => void;
  multiple?: boolean;
  disabled?: boolean;
  label?: string;
  accept?: string;
  size?: 'small' | 'medium';
  folderPath?: string;
  uploader?: (files: File[]) => Promise<string[]>;
  onUploadingChange?: (uploading: boolean) => void;
}> = ({
  onUploaded,
  multiple = true,
  disabled,
  label = 'Upload from device',
  accept = ATTACHMENT_ACCEPT,
  size = 'small',
  folderPath = ATTACHMENT_FOLDER,
  uploader,
  onUploadingChange,
}) => {
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    const tooBig = files.find(
      (f) => f.size > (isVideoFile(f) ? MAX_VIDEO_MB : MAX_ATTACHMENT_MB) * 1024 * 1024,
    );
    if (tooBig) {
      toast.error(
        `${tooBig.name} is larger than ${isVideoFile(tooBig) ? MAX_VIDEO_MB : MAX_ATTACHMENT_MB} MB.`,
      );
      return;
    }

    setUploading(true);
    try {
      const urls = uploader
        ? await uploader(files)
        : await uploadFilesToStorage(files, folderPath);
      const clean = (urls || []).filter(Boolean);
      if (!clean.length) throw new Error('Upload did not return a file URL');
      onUploaded(clean);
      toast.success(`${clean.length} file${clean.length === 1 ? '' : 's'} uploaded`);
    } catch (error: any) {
      console.error('attachment-upload-failed', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'File upload failed. Please try again.',
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <Button
      component="label"
      size={size}
      variant="outlined"
      disabled={disabled || uploading}
      startIcon={uploading ? <CircularProgress size={14} /> : <Upload size={15} />}
      sx={{
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 12,
        borderRadius: '8px',
        borderColor: C.border,
        color: C.ink,
        bgcolor: C.card,
        whiteSpace: 'nowrap',
        '&:hover': { borderColor: C.info, bgcolor: C.infoSoft },
      }}
    >
      {uploading ? 'Uploading...' : label}
      <input hidden type="file" accept={accept} multiple={multiple} onChange={handleChange} />
    </Button>
  );
};

export default AttachmentList;
